import { rufe } from './client';

/**
 * KUN-15: Wiedervorlagen mit Fälligkeit, Zuständigem, Priorität und
 * Erinnerung; die Übersicht „Meine Wiedervorlagen".
 */

export type Zustaendig =
  | { readonly art: 'person'; readonly id: string; readonly name: string }
  | { readonly art: 'rolle'; readonly kennung: string; readonly bezeichnung: string };

export type Zustaendigwunsch =
  | { readonly art: 'person'; readonly id: string }
  | { readonly art: 'rolle'; readonly kennung: string };

export type Bezugsart = 'angebot' | 'auftrag';

/** Vom Server gerechnet (FRS-16) — die Oberfläche rechnet keine Frist selbst. */
export type Faelligkeit = 'ueberfaellig' | 'faellig' | 'erinnert' | 'offen' | 'erledigt';

export type Listenstand = 'faellig' | 'ueberfaellig' | 'offen' | 'erledigt';

export type Prioritaet = 1 | 2 | 3;

export const PRIORITAETSNAME: Readonly<Record<Prioritaet, string>> = { 1: 'hoch', 2: 'normal', 3: 'niedrig' };

export const FAELLIGKEITSNAME: Readonly<Record<Faelligkeit, string>> = {
  ueberfaellig: 'überfällig',
  faellig: 'heute fällig',
  erinnert: 'Erinnerung',
  offen: 'offen',
  erledigt: 'erledigt',
};

export type Wiedervorlage = {
  readonly id: string;
  readonly kunde: { readonly id: string; readonly kundennummer: string; readonly name: string } | null;
  readonly bezug: { readonly art: Bezugsart; readonly id: string; readonly nummer: string | null } | null;
  readonly betreff: string;
  readonly beschreibung: string | null;
  /** Kalendertag JJJJ-MM-TT. */
  readonly faelligAm: string;
  readonly prioritaet: Prioritaet;
  readonly erinnerungAm: string | null;
  readonly zustaendig: Zustaendig;
  readonly angelegtAm: string;
  readonly angelegtVon: string | null;
  readonly erledigtAm: string | null;
  readonly erledigtVon: string | null;
  readonly erledigtVermerk: string | null;
  readonly faelligkeit: Faelligkeit;
  readonly darfErledigen: boolean;
};

export type Verlaufsart = 'angelegt' | 'zugewiesen' | 'verschoben' | 'geaendert' | 'erledigt' | 'wiedereroeffnet';

export const VERLAUFSNAME: Readonly<Record<Verlaufsart, string>> = {
  angelegt: 'angelegt',
  zugewiesen: 'zugewiesen',
  verschoben: 'verschoben',
  geaendert: 'geändert',
  erledigt: 'erledigt',
  wiedereroeffnet: 'wieder geöffnet',
};

export type Verlaufseintrag = {
  readonly zeitpunkt: string;
  readonly benutzer: string | null;
  readonly art: Verlaufsart;
  readonly angabe: string | null;
};

export type Zustaendigenauswahl = {
  readonly personen: readonly { readonly id: string; readonly name: string }[];
  readonly rollen: readonly { readonly kennung: string; readonly bezeichnung: string }[];
};

export type Wiedervorlageneingabe = {
  readonly betreff: string;
  readonly beschreibung: string | null;
  readonly faelligAm: string;
  readonly prioritaet: Prioritaet;
  readonly erinnerungAm: string | null;
  readonly zustaendig: Zustaendigwunsch;
};

export async function meineWiedervorlagen(stand: Listenstand): Promise<readonly Wiedervorlage[]> {
  return (await rufe<{ readonly wiedervorlagen: readonly Wiedervorlage[] }>(`/wiedervorlagen?stand=${stand}`))
    .wiedervorlagen;
}

export async function wiedervorlagenAmKunden(kunde: string): Promise<readonly Wiedervorlage[]> {
  return (await rufe<{ readonly wiedervorlagen: readonly Wiedervorlage[] }>(`/kunden/${kunde}/wiedervorlagen`))
    .wiedervorlagen;
}

export async function wiedervorlage(
  id: string,
): Promise<Wiedervorlage & { readonly verlauf: readonly Verlaufseintrag[] }> {
  return rufe(`/wiedervorlagen/${id}`);
}

export async function zustaendige(): Promise<Zustaendigenauswahl> {
  return rufe('/wiedervorlagen/zustaendige');
}

export async function wiedervorlageAnlegen(
  eingabe: Wiedervorlageneingabe & {
    readonly kunde: string | null;
    readonly bezug: { readonly art: Bezugsart; readonly id: string } | null;
  },
): Promise<{ readonly id: string }> {
  return rufe('/wiedervorlagen', { verfahren: 'POST', rumpf: eingabe });
}

export async function wiedervorlageAendern(id: string, eingabe: Wiedervorlageneingabe): Promise<void> {
  await rufe(`/wiedervorlagen/${id}`, { verfahren: 'PATCH', rumpf: eingabe });
}

export async function wiedervorlageErledigen(id: string, vermerk: string | null): Promise<void> {
  await rufe(`/wiedervorlagen/${id}/erledigen`, { verfahren: 'POST', rumpf: { vermerk } });
}

export async function wiedervorlageWiedereroeffnen(id: string): Promise<void> {
  await rufe(`/wiedervorlagen/${id}/wiedereroeffnen`, { verfahren: 'POST' });
}
